import { debounce, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Locale } from 'modules/domains/template';
import { LocaleLabel } from 'modules/templateEditor/common/LocaleLabel/LocaleLabel';
import { LocaleOption } from 'modules/templateEditor/common/types';
import { useFilterLocales } from 'modules/templateEditor/hooks/useFilterLocales';
import { useTranslations } from 'modules/templateEditor/hooks/useTranslations';
import { FormField } from 'ui/formField';
import { MultiFilterSelect } from 'ui/selects';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { Nullable, Nullish } from 'utils/tsHelpers';

import { formatDisplayValue, getLocaleOptions } from './LocaleMultiSelect.utils';

const SelectWrapper = styled.div`
  > div {
    width: 100%;
    > div {
      width: 100%;
      > div {
        width: 100%;
        > div {
          width: 100%;
        }
      }
    }
  }
`;

export const MAX_LOCALES_STR_LENGTH = 90;
export const LOCALE_MULTI_SELECT_TEST_ID = 'localeMultiSelect';

const DisplayValue = ({ text, filteredLocales }: { text: string; filteredLocales: Locale[] }) => (
  <div>
    {`${text}, `}
    <Tooltip
      isUnderlined
      placement={TipPlacement.Right}
      tooltipContent={
        <div>
          {filteredLocales.map((locale, idx) => (
            <LocaleLabel key={idx} locale={locale} />
          ))}
        </div>
      }
    >
      {`+${filteredLocales.length} more`}
    </Tooltip>
  </div>
);

type LocaleMultiSelectProps = {
  onSelect: (locales: string[]) => void;
  selectedDefaultLocale: Locale;
  activeTemplateLocales: Locale[];
  templateDefaultLocale?: Nullable<Locale>;
};

export const LocaleMultiSelect = ({
  onSelect,
  selectedDefaultLocale,
  activeTemplateLocales,
  templateDefaultLocale,
}: LocaleMultiSelectProps) => {
  const { t } = useTranslations();
  const activeTemplateLocaleTagNames = activeTemplateLocales.map((val) => `${val.tag}`);
  const [selectedLocaleTags, setSelectedLocaleTags] = useState<string[]>(
    activeTemplateLocaleTagNames,
  );
  const [defaultLocale, setDefaultLocale] = useState<Nullish<Locale>>(templateDefaultLocale);

  const handleSelect = (values: string[]) => {
    setSelectedLocaleTags(values);
    onSelect(values);
  };

  const localeOptions: LocaleOption[] = useMemo(
    () => getLocaleOptions(activeTemplateLocales, defaultLocale, t),
    [activeTemplateLocales, defaultLocale, t],
  );

  const displayText = useMemo(() => {
    const tags = selectedLocaleTags.join(', ');

    if (tags.length > MAX_LOCALES_STR_LENGTH) {
      const { text, filteredLocales } = formatDisplayValue(
        tags,
        MAX_LOCALES_STR_LENGTH,
        selectedLocaleTags,
        activeTemplateLocales,
      );

      return <DisplayValue text={text} filteredLocales={filteredLocales} />;
    } else {
      return tags;
    }
  }, [selectedLocaleTags, activeTemplateLocales]);

  useEffect(() => {
    if (!isEmpty(selectedDefaultLocale)) {
      setDefaultLocale(selectedDefaultLocale);
      const { tag: defaultLocaleTag } = selectedDefaultLocale;
      if (!selectedLocaleTags.includes(defaultLocaleTag)) {
        setSelectedLocaleTags([defaultLocaleTag, ...selectedLocaleTags]);
      }
    }
  }, [selectedDefaultLocale, selectedLocaleTags]);

  const { options, handleFilter } = useFilterLocales(
    localeOptions,
    t('label.defaultLocale') as string,
  );

  return (
    <FormField
      labelText={t('input.multiSelect.label')}
      data-test={`${LOCALE_MULTI_SELECT_TEST_ID}-form-field`}
    >
      <SelectWrapper>
        <MultiFilterSelect
          id={LOCALE_MULTI_SELECT_TEST_ID}
          inputContent={{
            filterPlaceholder: t('input.multiSelectFilter.placeholder'),
          }}
          onSearchChange={debounce(handleFilter, 300)}
          onSelect={(values) => {
            handleSelect(values);
          }}
          showAllCategories
          selectValueDisplayText={displayText}
          categoryLabels={{ Active: 'Active Locales' }}
          content={{
            clearText: t('input.multiSelect.actionClearText'),
            selectNone: t('input.multiSelect.actionSelectNone'),
            selectAll: t('input.multiSelect.actionSelectAll'),
          }}
          defaultValues={selectedLocaleTags}
          disableInternalFiltering
          emptyStateText={t('input.multiSelect.emptyState')}
          options={options}
          placeholder={t('input.multiSelect.placeholder')}
        />
      </SelectWrapper>
    </FormField>
  );
};
